// ============================================================
// A AVALIAÇÃO DOS 4 PILARES PELO CLIENTE (Story 34.3, Epic 34 — Tema #585)
//
// O usuário do cliente avalia, a cada trimestre, os 4 pilares de um profissional
// alocado. As decisões do tema que este ficheiro carrega:
//
//   2. ESCALA — nota INTEIRA de 1 a 10 por pilar; as médias mostram-se com uma casa.
//   3. CADÊNCIA — trimestral. A avaliação diz que trimestre avalia: o último
//      encerrado (o padrão) ou o corrente, nunca antes do início da alocação.
//   4. O CALIBER SCORE de um trimestre é a média simples dos 4 pilares.
//   5. A avaliação enviada NÃO SE EDITA; em cada trimestre vale a mais recente de
//      cada autor.
//
// É o contrato entre a tela, a API e as stories que leem o resumo (34.5 e 34.6): os
// pilares, o trimestre, o corpo do envio e a forma do resumo. A regra da contagem
// (a mais recente de cada autor) vive na API (`apps/api/src/lib/allocation-reviews.ts`).
// ============================================================
import { z } from 'zod'

import { isInEffectOn } from './allocation-live.ts'

/** Os 4 pilares, na ordem em que a tela os mostra. */
export const REVIEW_PILLARS = ['behavioral', 'technical', 'collaboration', 'delivery'] as const
export type ReviewPillar = (typeof REVIEW_PILLARS)[number]

/** O rótulo e o que cada pilar mede — as palavras do owner, no tema. */
export const REVIEW_PILLAR_META: Readonly<
  Record<ReviewPillar, { label: string; description: string }>
> = {
  behavioral: {
    label: 'Comportamental',
    description: 'Atitude, proatividade e adaptabilidade',
  },
  technical: {
    label: 'Técnico',
    description: 'Domínio de ferramentas e qualidade técnica',
  },
  collaboration: {
    label: 'Colaboração',
    description: 'Trabalho em equipe e comunicação',
  },
  delivery: {
    label: 'Entrega & Impacto',
    description: 'Resultados, autonomia e geração de valor',
  },
}

export const REVIEW_SCORE_MIN = 1
export const REVIEW_SCORE_MAX = 10

/** O comentário de um pilar e o geral. O geral cabe mais: é o resumo do trimestre. */
export const REVIEW_PILLAR_COMMENT_MAX = 500
export const REVIEW_COMMENT_MAX = 2000

// ------------------------------------------------------------------
// O TRIMESTRE — `AAAA-Qn`
// ------------------------------------------------------------------
// Texto, e não um par de números: ordena-se lexicograficamente igual à ordem do
// calendário (`2025-Q4` < `2026-Q1`), como as datas de contrato `AAAA-MM-DD`. As
// funções abaixo NÃO leem o relógio: quem chama passa o `hoje` (o dia em São Paulo),
// como em `allocation-live.ts`.

export const ReviewQuarterSchema = z
  .string()
  .regex(/^\d{4}-Q[1-4]$/, 'Trimestre no formato AAAA-Qn')

/** O trimestre de um dia `AAAA-MM-DD`. */
export function quarterOf(dia: string): string {
  const ano = dia.slice(0, 4)
  const mes = Number(dia.slice(5, 7))
  return `${ano}-Q${Math.ceil(mes / 3)}`
}

function partes(quarter: string): { ano: number; n: number } {
  return { ano: Number(quarter.slice(0, 4)), n: Number(quarter.slice(6)) }
}

/** O primeiro dia do trimestre, `AAAA-MM-DD`. */
export function quarterStart(quarter: string): string {
  const { ano, n } = partes(quarter)
  return `${ano}-${String((n - 1) * 3 + 1).padStart(2, '0')}-01`
}

/** O último dia do trimestre, `AAAA-MM-DD`. Março e dezembro têm 31; junho e setembro, 30. */
export function quarterEnd(quarter: string): string {
  const { ano, n } = partes(quarter)
  return `${ano}-${String(n * 3).padStart(2, '0')}-${n === 1 || n === 4 ? '31' : '30'}`
}

/** O trimestre de antes. O do primeiro é o último do ano anterior. */
export function previousQuarter(quarter: string): string {
  const { ano, n } = partes(quarter)
  return n === 1 ? `${ano - 1}-Q4` : `${ano}-Q${n - 1}`
}

/** «3º trimestre de 2026» — o que a tela e o apontamento do Journal dizem. */
export function quarterLabel(quarter: string): string {
  const { ano, n } = partes(quarter)
  return `${n}º trimestre de ${ano}`
}

/**
 * O prazo da avaliação de um trimestre (decisão 11 do Tema #585): o dia 15 do mês
 * seguinte ao fim dele. O 4º trimestre de 2026 vence a 15 de janeiro de 2027.
 */
export function prazoDaAvaliacao(quarter: string): string {
  const fim = quarterEnd(quarter)
  const ano = Number(fim.slice(0, 4))
  const mes = Number(fim.slice(5, 7))
  return mes === 12 ? `${ano + 1}-01-15` : `${ano}-${String(mes + 1).padStart(2, '0')}-15`
}

/**
 * O trimestre é RECENTE no dia `dia` (Story 38.8): o corrente, o último encerrado ou
 * o anterior a ele. É a única noção de «recente» do Dashboard de gestão de contas:
 * um Caliber Score de um trimestre mais antigo já não descreve a carteira de hoje.
 * Tolera um trimestre sem avaliação, que é o caso entre o fim dele e o prazo.
 */
export function isRecentQuarter(quarter: string, dia: string): boolean {
  return quarter >= previousQuarter(previousQuarter(quarterOf(dia)))
}

/** Um trimestre que se pode avaliar. `current` é o corrente; o outro, o último encerrado. */
export interface ReviewableQuarter {
  quarter: string
  current: boolean
}

/**
 * Os trimestres que se podem avaliar em `hoje`, do mais antigo para o mais novo: o
 * último encerrado e o corrente (decisão 3).
 *
 * ⚠️ «NUNCA ANTES DO INÍCIO» é o trimestre que ACABA antes do início da alocação:
 * quem começou a 30 de setembro trabalhou um dia do 3º trimestre, e esse trimestre
 * conta. Fora de vigor não há nenhum — nem a alocação por começar, nem a encerrada:
 * é a regra do portal, que só vê a alocação em vigor (`isInEffectOn`, Story 29.2).
 */
export function reviewableQuarters(
  startedOn: string,
  endedOn: string | null,
  hoje: string,
): ReviewableQuarter[] {
  if (!isInEffectOn(startedOn, endedOn, hoje)) return []
  const corrente = quarterOf(hoje)
  const anterior = previousQuarter(corrente)
  return [
    ...(quarterEnd(anterior) >= startedOn ? [{ quarter: anterior, current: false }] : []),
    { quarter: corrente, current: true },
  ]
}

// ------------------------------------------------------------------
// O ENVIO
// ------------------------------------------------------------------

const ScoreSchema = z.number().int().min(REVIEW_SCORE_MIN).max(REVIEW_SCORE_MAX)
const PillarCommentSchema = z.string().trim().max(REVIEW_PILLAR_COMMENT_MAX)

/**
 * `POST /api/portal/allocations/:id/reviews` — as 4 notas, o trimestre e os
 * comentários (o de cada pilar e o geral, todos opcionais).
 *
 * ⚠️ SEM `.strict()`, pela razão escrita em `CreateAllocationNoteSchema` (dtos.ts):
 * o autor, o lado, a pessoa e a alocação NÃO são declarados, e o zod descarta-os. O
 * corpo pode trazer `authorUserId` à vontade que ele não chega a `parsed.data`; o
 * servidor tira-os da sessão e da linha da alocação.
 *
 * O trimestre aqui é só a FORMA. Se ele se pode avaliar (o último encerrado ou o
 * corrente, nunca antes do início) depende da alocação, e decide-o a rota.
 */
export const CreateAllocationReviewSchema = z.object({
  quarter: ReviewQuarterSchema,
  scores: z.object({
    behavioral: ScoreSchema,
    technical: ScoreSchema,
    collaboration: ScoreSchema,
    delivery: ScoreSchema,
  }),
  comments: z
    .object({
      behavioral: PillarCommentSchema.optional(),
      technical: PillarCommentSchema.optional(),
      collaboration: PillarCommentSchema.optional(),
      delivery: PillarCommentSchema.optional(),
    })
    .optional(),
  comment: z.string().trim().max(REVIEW_COMMENT_MAX).optional(),
})
export type CreateAllocationReview = z.infer<typeof CreateAllocationReviewSchema>

// ------------------------------------------------------------------
// O RESUMO — o que as stories 34.5 (score e cartões) e 34.6 (evolução) leem
// ------------------------------------------------------------------

/**
 * Um trimestre avaliado. Só entram os trimestres com pelo menos uma avaliação: sem
 * avaliação não há média, e a tela diz que ainda não há em vez de mostrar zero.
 */
export interface ReviewQuarterSummary {
  quarter: string
  /** Quantos autores contam — a avaliação mais recente de cada um (decisão 5). */
  reviewers: number
  /** A média de cada pilar, com uma casa. */
  pillars: Record<ReviewPillar, number>
  /** O Caliber Score: a média dos 4 pilares, com uma casa (decisão 4). */
  score: number
}

/**
 * `GET /api/portal/allocations/:id/reviews/summary` e o interno
 * `GET /api/allocations/:id/reviews/summary` — a MESMA forma nos dois.
 *
 * Sem autores: o resumo é a média, e a tela mostra a média (decisão 1).
 */
export interface AllocationReviewSummary {
  /** Os trimestres avaliados, do mais antigo para o mais novo. */
  quarters: ReviewQuarterSummary[]
  /** O que se pode avaliar hoje, do mais antigo para o mais novo. Vazio fora de vigor. */
  reviewableQuarters: ReviewableQuarter[]
}

// ------------------------------------------------------------------
// A TENDÊNCIA E A VARIAÇÃO — a regra do painel (Story 34.5), partilhada (Story 38.8)
// ------------------------------------------------------------------
// Vinham de `apps/web/src/routes/portal/allocations/painel/avaliacoes.ts`, que as
// reexporta. O Dashboard de gestão de contas usa-as na API: a mesma pessoa não pode
// estar «Evoluindo» no painel dela e «Estável» no dashboard.

export type Tendencia = 'evoluindo' | 'estavel' | 'em-queda'

/**
 * A diferença que conta como mudança: 0,2 numa escala de 10. Abaixo disso é ruído
 * de quem avaliou (uma nota inteira a mais num pilar move a média 0,25).
 */
export const LIMIAR_DA_TENDENCIA = 0.2

/** Sem trimestre anterior, não há tendência — e a tela não inventa uma. */
export function tendencia(atual: number, anterior: number | null): Tendencia | null {
  if (anterior === null) return null
  const diferenca = Math.round((atual - anterior) * 10) / 10
  if (diferenca >= LIMIAR_DA_TENDENCIA) return 'evoluindo'
  if (diferenca <= -LIMIAR_DA_TENDENCIA) return 'em-queda'
  return 'estavel'
}

export const ROTULO_DA_TENDENCIA: Record<Tendencia, string> = {
  evoluindo: 'Evoluindo',
  estavel: 'Estável',
  'em-queda': 'Em queda',
}

/** «+0.4», «−0.3», «0.0» — a variação com sinal, na casa decimal do score. */
export function variacao(atual: number, anterior: number): string {
  const d = Math.round((atual - anterior) * 10) / 10
  if (d === 0) return '0.0'
  return `${d > 0 ? '+' : '−'}${Math.abs(d).toFixed(1)}`
}

/** «+2», «−1», «0» — a variação de uma contagem. */
export function variacaoInteira(atual: number, anterior: number): string {
  const d = atual - anterior
  if (d === 0) return '0'
  return `${d > 0 ? '+' : '−'}${Math.abs(d)}`
}
